// src/components/SectionNav.js

import React, { useState, useEffect } from 'react';
import './SectionNav.css';

const SECTIONS = [
  { id: 'big-story', label: 'The Big Story' },
  { id: 'world-affairs', label: 'World' },
  { id: 'us-policy', label: 'US Policy' },
  { id: 'business', label: 'Business' },
  { id: 'london-ottawa', label: 'London & Ottawa' },
  { id: 'on-this-day', label: 'On This Day' },
  { id: 'business-breakdown', label: 'Deep Dive' },
  { id: 'quick-hits', label: 'Quick Hits' },
];

const SectionNav = () => {
  const [activeSection, setActiveSection] = useState('big-story');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    SECTIONS.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const navigateToAuthors = () => {
    window.location.hash = 'authors';
    setMobileMenuOpen(false);
  };

  return (
    <nav className="section-nav" aria-label="Section navigation">
      {/* Desktop: Horizontal bar */}
      <div className="section-nav-bar">
        <ul className="section-nav-list">
          {SECTIONS.map(({ id, label }) => (
            <li key={id}>
              <button
                className={`section-nav-link ${activeSection === id ? 'active' : ''}`}
                onClick={() => scrollToSection(id)}
                aria-current={activeSection === id ? 'true' : undefined}
              >
                {label}
              </button>
            </li>
          ))}
          <li className="section-nav-divider" aria-hidden="true"></li>
          <li>
            <button className="section-nav-link section-nav-link--authors" onClick={navigateToAuthors}>
              Our Writers
            </button>
          </li>
        </ul>
      </div>

      {/* Mobile: Burger button */}
      <button
        className={`burger-button ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileMenuOpen}
      >
        <span className="burger-line"></span>
        <span className="burger-line"></span>
        <span className="burger-line"></span>
      </button>

      {/* Mobile: Slide-out menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <span className="mobile-menu-title">Sections</span>
          <button
            className="mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <ul className="mobile-menu-list">
          {SECTIONS.map(({ id, label }) => (
            <li key={id}>
              <button
                className={`mobile-menu-link ${activeSection === id ? 'active' : ''}`}
                onClick={() => scrollToSection(id)}
              >
                {label}
              </button>
            </li>
          ))}
          <li className="mobile-menu-divider"></li>
          <li>
            <button className="mobile-menu-link mobile-menu-link--authors" onClick={navigateToAuthors}>
              Meet Our Writers
            </button>
          </li>
        </ul>
      </div>

      {/* Mobile: Backdrop */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </nav>
  );
};

export default SectionNav;
